import { v4 as uuid } from 'uuid';
import { isEmpty } from 'lodash';
import { getCurrency } from '../../../utils';
import { parseDescriptionAndBullets, sortPlan } from '../billing/planUtils';
import {
    apiGetConnectCurrentPlan,
    apiGetConnectPlanDetails,
    apiPreviewSubscription,
    apiCreateSubscription,
    apiUpgradeSubscription,
    apiFinalize3DSecure,
    apiBrowseConnectIntegrations,
    apiGetConnectToken,
    apiCancelSubscription,
    apiGetIntegrationDetails,
} from './api';

const ERR_3D_SECURE = 'Your card was declined. This transaction requires 3D secure authentication.';

/* Pull a real error out of the settled response, or null when there isn't one. */
function extractErrors (res) {
    if (res && res.data && Array.isArray(res.data.errors) && res.data.errors.length) {
        return res.data.errors;
    }
    if (res && res.data && (res.data.error || res.data.message)) {
        return [res.data];
    }
    return null;
}

function isOk (res) {
    return !!(res && res.status >= 200 && res.status < 300);
}

/*
 * Settle a failed Connect action into an inline `{ error }` result.
 * The api layer's dispatchErrors() only recognises GQL-shaped errors, so a
 * plain REST error body arrives here undispatched and we dispatch it ourselves.
 * When the response carries nothing usable, the api layer either already
 * dispatched a GQL error (don't overwrite it) or the request failed before
 * responding — reuse what it dispatched, otherwise surface the generic
 * fallback. Pass `dispatch: false` for actions that surface errors inline.
 */
function dispatchFailure (errorStore, { action, fallback, dispatch }, res) {
    const errors = extractErrors(res);
    if (errors) {
        if (dispatch) {
            errorStore.dispatchError(action, errors);
        }
        return { error: errors[0] };
    }
    const dispatched = errorStore.errors;
    if (dispatched && dispatched.length) {
        return { error: dispatched[0] };
    }
    if (dispatch) {
        errorStore.dispatchError(action, [{ message: fallback }]);
    }
    return { error: { message: fallback } };
}

/*
 * Shared envelope for Connect store actions. Stamps the errorStore action,
 * toggles the loading token (when `loadingKey` is given), runs `request`, and
 * routes failures through dispatchFailure. `onSuccess(res)` returns the result
 * object (e.g. `{ data }`) when it considers the response a success, or
 * `undefined` to fall through to the error path.
 */
async function runConnectAction (store, { action, loadingKey, fallback, request, onSuccess, dispatch = true }) {
    const errorStore = store.env.errorStore;
    errorStore.action = action;
    if (loadingKey) {
        store.observed[loadingKey] = true;
    }
    try {
        const res = await request(store.env.userStore.publisher);
        const success = onSuccess(res);
        return success !== undefined ? success : dispatchFailure(errorStore, { action, fallback, dispatch }, res);
    } catch (e) {
        return dispatchFailure(errorStore, { action, fallback, dispatch }, e && e.response);
    } finally {
        if (loadingKey) {
            store.observed[loadingKey] = false;
        }
    }
}

/*
 * Detect the 3D-secure-required chargify error inside a createSubscription /
 * upgradeSubscription response, mirroring billing's get3DSecureInfo(). `key`
 * is the GQL mutation field the result is nested under.
 */
function get3DSecureInfo (res, key) {
    const data = {
        is3DSecure: false,
        actionLink: '',
    };

    const container = res && res.data && res.data.data && res.data.data[key];
    const edges = container && container.edges;
    if (edges && edges.length > 0) {
        const result = edges[0].node;
        if (result.chargifyResult && !isEmpty(result.chargifyResult.errors)) {
            const chargifyErr = result.chargifyResult.errors[0];
            if (chargifyErr === ERR_3D_SECURE) {
                data.actionLink = result.chargifyResult.actionLink;
                data.gatewayPaymentId = result.chargifyResult.gatewayPaymentId;
                data.is3DSecure = true;
                return data;
            }
            if (chargifyErr) {
                data.hasCardError = true;
                data.chargifyErr = chargifyErr;
            }
        }
    }
    return data;
}

export async function getCurrentPlan () {
    await runConnectAction(this, {
        action: 'connectGetCurrentPlan',
        loadingKey: 'currentPlanLoading',
        fallback: 'Failed to load Connect plan.',
        request: (publisher) => apiGetConnectCurrentPlan({
            token: publisher.token,
            traceId: this.env.traceId,
        }),
        onSuccess: (res) => {
            if (!isOk(res)) return undefined;
            const data = res.data && res.data.data;
            if (!data) {
                this.currentPlan = null;
                this.observed.currentPlan = uuid();
                return true;
            }
            this.currentPlan = {
                name: data.name,
                code: data.code,
                price: data.price,
                status: data.status,
                extras: {
                    transactionLimit: data.transactionLimit,
                    transactionUsed: data.transactionUsed,
                    transactionRemaining: data.transactionRemaining,
                },
                nextRenewalDate: data.nextRenewalDate,
                cancellationScheduledAt: data.cancellationScheduledAt,
                nextPlanName: data.nextPlanName,
            };
            this.observed.currentPlan = uuid();
            return true;
        },
    });
}

export async function getPlanDetails () {
    await runConnectAction(this, {
        action: 'connectGetPlanDetails',
        loadingKey: 'plansLoading',
        fallback: 'Failed to load Connect plans.',
        request: (publisher) => apiGetConnectPlanDetails({
            input: { currency: getCurrency(this.env) },
            token: publisher.token,
            traceId: this.env.traceId,
        }),
        onSuccess: (res) => {
            if (!isOk(res) || !res.data || !res.data.data || !res.data.data.getPlanDetails) {
                return undefined;
            }
            const edges = res.data.data.getPlanDetails.edges;
            const plans = edges.map((edge) => {
                const { description, bullets } = parseDescriptionAndBullets(edge.node.description);
                return {
                    ...edge.node,
                    description,
                    bullets,
                    id: edge.node.id || '-',
                };
            });
            this.availablePlans = sortPlan(plans);
            this.observed.availablePlans = uuid();
            return true;
        },
    });
}

export async function previewSubscription (input, { basePrice, productHandle, productName } = {}) {
    // No loading token (the checkout drawer manages its own pending state) and
    // errors are surfaced inline rather than dispatched to the error store.
    return runConnectAction(this, {
        action: 'connectPreviewSubscription',
        fallback: 'Failed to preview subscription.',
        dispatch: false,
        request: (publisher) => apiPreviewSubscription({
            input,
            token: publisher.token,
            traceId: this.env.traceId,
            basePrice,
            productHandle,
            productName,
        }),
        onSuccess: (res) => {
            if (isOk(res)) {
                const node = res.data && res.data.data && res.data.data.previewSubscription && res.data.data.previewSubscription.edges &&
                    res.data.data.previewSubscription.edges[0] && res.data.data.previewSubscription.edges[0].node;
                return { data: node };
            }
            return undefined;
        },
    });
}

export async function createSubscription ({ code, addOns = [], ott, firstName, lastName, country, state, zip, city, couponCodes } = {}) {
    return runConnectAction(this, {
        action: 'connectCreateSubscription',
        fallback: 'Failed to create Connect subscription.',
        dispatch: false,
        request: (publisher) => apiCreateSubscription({
            input: {
                provider: 'chargify',
                currency: getCurrency(this.env),
                code,
                addOns,
                ott,
                firstName,
                lastName,
                country,
                state,
                zip,
                city,
                ...(couponCodes ? { couponCodes } : {}),
            },
            token: publisher.token,
            traceId: this.env.traceId,
        }),
        onSuccess: (res) => {
            if (!isOk(res)) return undefined;
            const edges = res.data && res.data.data && res.data.data.createSubscription && res.data.data.createSubscription.edges;
            const node = edges && edges[0] && edges[0].node;
            if (!node) return undefined;

            const { is3DSecure, actionLink, gatewayPaymentId, hasCardError, chargifyErr } = get3DSecureInfo(res, 'createSubscription');
            if (is3DSecure) {
                this.chargifyGatewayPaymentId = gatewayPaymentId;
                this.chargifyActionLink = actionLink;
                this.observed.chargifyActionLink = uuid();
                return { is3DSecure: true };
            }
            if (hasCardError) {
                return { error: { message: chargifyErr } };
            }
            return { data: node };
        },
    });
}

export async function upgradeSubscription ({ code, addOns = [], ott } = {}) {
    return runConnectAction(this, {
        action: 'connectUpgradeSubscription',
        fallback: 'Failed to update Connect subscription.',
        dispatch: false,
        request: (publisher) => apiUpgradeSubscription({
            input: {
                provider: 'chargify',
                currency: getCurrency(this.env),
                code,
                addOns,
                ott,
            },
            token: publisher.token,
            traceId: this.env.traceId,
        }),
        onSuccess: (res) => {
            if (!isOk(res)) return undefined;
            const edges = res.data && res.data.data && res.data.data.upgradeSubscription && res.data.data.upgradeSubscription.edges;
            const node = edges && edges[0] && edges[0].node;
            if (!node) return undefined;

            const { is3DSecure, actionLink, gatewayPaymentId, hasCardError, chargifyErr } = get3DSecureInfo(res, 'upgradeSubscription');
            if (is3DSecure) {
                this.chargifyGatewayPaymentId = gatewayPaymentId;
                this.chargifyActionLink = actionLink;
                this.observed.chargifyActionLink = uuid();
                return { is3DSecure: true };
            }
            if (hasCardError) {
                return { error: { message: chargifyErr } };
            }
            return { data: node };
        },
    });
}

export async function finalize3DSecure ({ code, addOns = [], ott, firstName, lastName } = {}) {
    return runConnectAction(this, {
        action: 'connectFinalize3DSecureSubscription',
        fallback: 'Failed to finalize Connect subscription.',
        dispatch: false,
        request: (publisher) => apiFinalize3DSecure({
            input: {
                provider: 'chargify',
                currency: getCurrency(this.env),
                code,
                addOns,
                ott,
                firstName,
                lastName,
                ...(this.chargifyGatewayPaymentId ? { gatewayPaymentId: this.chargifyGatewayPaymentId } : {}),
            },
            token: publisher.token,
            traceId: this.env.traceId,
        }),
        onSuccess: (res) => {
            if (!isOk(res)) return undefined;
            const edges = res.data && res.data.data && res.data.data.finalize3DSecureSubscription &&
                res.data.data.finalize3DSecureSubscription.edges;
            const node = edges && edges[0] && edges[0].node;
            if (!node) return undefined;
            return { data: node, is3DSecure: false };
        },
    });
}

export async function browseConnectIntegrations () {
    await runConnectAction(this, {
        action: 'browseConnectIntegrations',
        loadingKey: 'integrationsLoading',
        fallback: 'Failed to load integrations.',
        request: (publisher) => apiBrowseConnectIntegrations({
            token: publisher.token,
            traceId: this.env.traceId,
            action: 'browseConnectIntegrations',
        }),
        onSuccess: (res) => {
            if (isOk(res) && res.data && res.data.data !== undefined) {
                this.integrations = res.data.data || [];
                this.observed.integrations = uuid();
                return true;
            }
            return undefined;
        },
    });
}

/*
 * CDN-hosted "more details" copy (subtitle/summary/features) for integrations.
 * Static, publisher-agnostic content — no token/traceId/error dispatch needed,
 * apiGetIntegrationDetails never throws (it resolves to {} on failure).
 */
export async function getIntegrationDetails () {
    this.observed.integrationDetailsLoading = true;
    this.integrationDetails = await apiGetIntegrationDetails();
    this.observed.integrationDetails = uuid();
    this.observed.integrationDetailsLoading = false;
}

export async function getConnectToken () {
    await runConnectAction(this, {
        action: 'getConnectToken',
        loadingKey: 'connectTokenLoading',
        fallback: 'Failed to fetch Connect token.',
        request: (publisher) => apiGetConnectToken({
            token: publisher.token,
            traceId: this.env.traceId,
            action: 'getConnectToken',
        }),
        onSuccess: (res) => {
            if (isOk(res) && res.data && res.data.data && res.data.data.token) {
                this.connectToken = res.data.data.token;
                this.observed.connectToken = uuid();
                return true;
            }
            return undefined;
        },
    });
}

export async function cancelSubscription () {
    return runConnectAction(this, {
        action: 'connectCancelSubscription',
        fallback: 'Failed to cancel Connect subscription.',
        request: (publisher) => apiCancelSubscription({
            input: { currency: getCurrency(this.env) },
            token: publisher.token,
            traceId: this.env.traceId,
        }),
        onSuccess: (res) => {
            if (!isOk(res)) return undefined;
            const edges = res.data && res.data.data && res.data.data.cancelSubscription &&
                res.data.data.cancelSubscription.edges;
            const node = edges && edges[0] && edges[0].node;
            if (!node) return undefined;
            return { data: node };
        },
    });
}
